/**
 * SBSettings.jsx – v5
 * CHANGES:
 *   - Charge Heads tab: removed chargeType and defaultAmount fields
 *     (these now live per-member under the Members tab)
 *   - Charge Heads now only manage: name, code, YEA mapping, GST, display order
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Zap, AlertCircle, Calendar, FileText, Mail } from 'lucide-react';
import {
  getChargeHeads, createChargeHead, updateChargeHead,
  deleteChargeHead, seedChargeHeads,
  getBillingSettings, saveBillingSettings,
  getTerms, saveTerms,
} from '../../api/societybook.api';
import { getHeads } from '../../api/yea.api';
import { getSociety, updateSociety } from '../../api/society.api';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import Modal  from '../../components/common/Modal';
import toast  from 'react-hot-toast';

const EMPTY_FORM = {
  name: '', code: '', isGSTApplicable: false, gstRate: 0,
  yea_incomeHead: '', description: '', displayOrder: '',
};

const FREQUENCY_OPTIONS = [
  { key: 'monthly',     label: 'Monthly',     desc: 'One bill per month for every member',                 icon: '📅', monthCount: 1    },
  { key: 'quarterly',   label: 'Quarterly',   desc: 'Bills generated every 3 months (Apr, Jul, Oct, Jan)', icon: '🗓️', monthCount: 3    },
  { key: 'half_yearly', label: 'Half-Yearly', desc: 'Bills generated every 6 months (Apr–Sep, Oct–Mar)',   icon: '📆', monthCount: 6    },
  { key: 'custom',      label: 'Custom',      desc: 'Manually select billing periods each time',            icon: '⚙️', monthCount: null },
];

const SETTINGS_TAB_ITEMS = [
  { key: 'chargeheads', label: 'Charge Heads',       icon: <Zap size={14} />      },
  { key: 'frequency',   label: 'Billing Frequency',  icon: <Calendar size={14} /> },
  { key: 'terms',       label: 'Terms & Conditions', icon: <FileText size={14} /> },
  { key: 'email',       label: 'Email Settings',     icon: <Mail size={14} />     },
];

const DEFAULT_TERMS = `1. Maintenance charges are due by the due date mentioned on the bill.
2. Interest will be charged on delayed payments as per society rules.
3. Cheques should be drawn in favour of the society.
4. For NEFT/UPI transfers, please share the UTR number with the office.
5. All disputes are subject to local jurisdiction.`;

export default function SBSettings({ societyId }) {
  const [activeTab, setActiveTab] = useState('chargeheads');

  // ── Charge Heads state ─────────────────────────────────────────────────────
  const [heads,     setHeads]     = useState([]);
  const [yeaHeads,  setYeaHeads]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editHead,  setEditHead]  = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [seeding,   setSeeding]   = useState(false);

  // ── Billing Frequency state ────────────────────────────────────────────────
  const [frequency,       setFrequency]       = useState('monthly');
  const [customPeriods,   setCustomPeriods]   = useState([]);
  const [freqSaving,      setFreqSaving]      = useState(false);
  const [freqLoading,     setFreqLoading]     = useState(false);
  const [newPeriodLabel,  setNewPeriodLabel]  = useState('');
  const [newPeriodMonths, setNewPeriodMonths] = useState([]);

  // ── Terms & Conditions state ───────────────────────────────────────────────
  const [terms,        setTerms]        = useState('');
  const [termsSaving,  setTermsSaving]  = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);

  // ── Email Settings state ───────────────────────────────────────────────────
  const [replyToEmail, setReplyToEmail] = useState('');
  const [emailSaving,  setEmailSaving]  = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chRes, yeaRes] = await Promise.all([
        getChargeHeads(societyId),
        getHeads(societyId, 'income').catch(() => ({ data: { heads: [] } })),
      ]);
      setHeads(chRes.data.data || []);
      setYeaHeads(yeaRes.data.heads || []);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, [societyId]);

  const loadFrequency = useCallback(async () => {
    setFreqLoading(true);
    try {
      const res = await getBillingSettings(societyId);
      const s = res.data.data || {};
      setFrequency(s.frequency || 'monthly');
      setCustomPeriods(s.customPeriods || []);
    } catch {} finally { setFreqLoading(false); }
  }, [societyId]);

  const loadTerms = useCallback(async () => {
    setTermsLoading(true);
    try {
      const res = await getTerms(societyId);
      setTerms(res.data.data || DEFAULT_TERMS);
    } catch { setTerms(DEFAULT_TERMS); }
    finally { setTermsLoading(false); }
  }, [societyId]);

  const loadEmailSettings = useCallback(async () => {
    setEmailLoading(true);
    try {
      const res = await getSociety(societyId);
      setReplyToEmail(res.data.society?.replyToEmail || '');
    } catch {} finally { setEmailLoading(false); }
  }, [societyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activeTab === 'frequency') loadFrequency(); }, [activeTab, loadFrequency]);
  useEffect(() => { if (activeTab === 'terms') loadTerms(); }, [activeTab, loadTerms]);
  useEffect(() => { if (activeTab === 'email') loadEmailSettings(); }, [activeTab, loadEmailSettings]);

  // ── Charge Head handlers ───────────────────────────────────────────────────
  const openAdd = () => {
    setEditHead(null);
    setForm({ ...EMPTY_FORM, displayOrder: heads.length + 1 });
    setShowModal(true);
  };

  const openEdit = h => {
    setEditHead(h);
    setForm({
      name: h.name, code: h.code,
      isGSTApplicable: h.isGSTApplicable,
      gstRate: h.gstRate || 0,
      yea_incomeHead: h.yea_incomeHead || '',
      description: h.description || '',
      displayOrder: h.displayOrder || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error('Name and Code are required'); return; }
    try {
      setSaving(true);
      const payload = {
        ...form, societyId,
        gstRate:      parseFloat(form.gstRate) || 0,
        displayOrder: parseInt(form.displayOrder) || 0,
      };
      if (editHead) await updateChargeHead(editHead._id, payload);
      else          await createChargeHead(payload);
      toast.success(editHead ? 'Updated' : 'Added');
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || err.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async h => {
    if (!window.confirm(`Deactivate "${h.name}"?`)) return;
    try { await deleteChargeHead(h._id); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  const handleSeed = async () => {
    if (!window.confirm('Seed default charge heads? Existing ones will not be duplicated.')) return;
    try {
      setSeeding(true);
      const res = await seedChargeHeads(societyId);
      toast.success(`${res.data.inserted} charge head(s) added`);
      load();
    } catch { toast.error('Failed to seed'); }
    finally { setSeeding(false); }
  };

  const sf = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  // ── Frequency handlers ─────────────────────────────────────────────────────
  const toggleCustomMonth = idx => {
    setNewPeriodMonths(prev =>
      prev.includes(idx) ? prev.filter(m => m !== idx) : [...prev, idx]
    );
  };

  const addCustomPeriod = () => {
    if (!newPeriodLabel.trim() || newPeriodMonths.length === 0) {
      toast.error('Enter a label and select at least one month'); return;
    }
    setCustomPeriods(prev => [...prev, { label: newPeriodLabel.trim(), months: [...newPeriodMonths].sort((a, b) => a - b) }]);
    setNewPeriodLabel('');
    setNewPeriodMonths([]);
  };

  const removeCustomPeriod = idx =>
    setCustomPeriods(prev => prev.filter((_, i) => i !== idx));

  const handleFreqSave = async () => {
    try {
      setFreqSaving(true);
      await saveBillingSettings(societyId, { frequency, customPeriods });
      toast.success('Billing settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setFreqSaving(false); }
  };

  // ── Terms handler ──────────────────────────────────────────────────────────
  const handleTermsSave = async () => {
    try {
      setTermsSaving(true);
      await saveTerms(societyId, terms);
      toast.success('Terms saved');
    } catch { toast.error('Failed to save terms'); }
    finally { setTermsSaving(false); }
  };

  // ── Email Settings handler ─────────────────────────────────────────────────
  const handleEmailSave = async () => {
    try {
      setEmailSaving(true);
      await updateSociety(societyId, { replyToEmail });
      toast.success('Email settings saved');
    } catch { toast.error('Failed to save email settings'); }
    finally { setEmailSaving(false); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Tab bar */}
      <div className="tab-bar" style={{ marginBottom: '1.5rem' }}>
        {SETTINGS_TAB_ITEMS.map(t => (
          <button key={t.key} className={`tab-btn ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}>
            {t.icon}<span style={{ marginLeft: 6 }}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* ══ TAB: Charge Heads ══ */}
      {activeTab === 'chargeheads' && (
        <div>
          {/* Info banner */}
          <div style={{ padding: '0.75rem 1rem', background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(99,179,237,0.2)', marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--info)', display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Charge heads define the <strong>categories</strong> of maintenance charges (e.g. Service Charges, Sinking Fund).
              Per-flat charge types and amounts are now set under <strong>Members → Charges</strong>.
            </span>
          </div>

          {loading ? <div className="loading-center"><div className="spinner" /></div> : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{heads.length} charge head(s) configured</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" variant="subtle" loading={seeding} onClick={handleSeed}>Seed Defaults</Button>
                  <Button size="sm" icon={Plus} onClick={openAdd}>Add Charge Head</Button>
                </div>
              </div>

              {heads.length === 0 ? (
                <div className="empty-state">
                  <AlertCircle size={28} />
                  <p>No charge heads yet. Seed defaults or add manually.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {heads.map(h => (
                    <div key={h._id} className="section-card" style={{ padding: '0.85rem 1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{h.name}</span>
                          <span className="badge badge-subtle" style={{ marginLeft: 8, fontSize: '0.7rem' }}>{h.code}</span>
                          {h.isGSTApplicable && (
                            <span className="badge badge-accent" style={{ marginLeft: 4, fontSize: '0.7rem' }}>GST {h.gstRate}%</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button size="xs" variant="subtle" onClick={() => openEdit(h)}>Edit</Button>
                          <Button size="xs" variant="danger" icon={Trash2} onClick={() => handleDelete(h)} />
                        </div>
                      </div>
                      {h.yea_incomeHead ? (
                        <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: 4 }}>
                          YEA → {h.yea_incomeHead}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: 4 }}>
                          ⚠ Not mapped to YEA
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ TAB: Billing Frequency ══ */}
      {activeTab === 'frequency' && (
        <div>
          {freqLoading ? <div className="loading-center"><div className="spinner" /></div> : (
            <>
              <div className="section-card" style={{ marginBottom: '1.25rem' }}>
                <div className="section-card-head"><h3>Billing Frequency</h3></div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  Choose how often maintenance bills are generated for this society.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                  {FREQUENCY_OPTIONS.map(opt => (
                    <div key={opt.key} onClick={() => setFrequency(opt.key)}
                      style={{ padding: '1rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer', border: `2px solid ${frequency === opt.key ? 'var(--gold)' : 'var(--border-subtle)'}`, background: frequency === opt.key ? 'var(--gold-glow)' : 'var(--bg-raised)', transition: 'all var(--t-fast)' }}>
                      <div style={{ fontSize: '1.2rem', marginBottom: 4 }}>{opt.icon}</div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: frequency === opt.key ? 'var(--gold)' : 'var(--text)' }}>{opt.label}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {frequency === 'custom' && (
                <div className="section-card" style={{ marginBottom: '1.25rem' }}>
                  <div className="section-card-head"><h3>Custom Periods</h3></div>
                  {customPeriods.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: '1rem' }}>
                      {customPeriods.map((p, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                          <div>
                            <span style={{ fontWeight: 600, fontSize: '0.875rem', marginRight: '0.75rem' }}>{p.label}</span>
                            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.3rem' }}>
                              {(p.months || []).map(mIdx => (
                                <span key={mIdx} className="badge badge-subtle" style={{ fontSize: '0.7rem' }}>{MONTHS_SHORT[mIdx]}</span>
                              ))}
                            </div>
                          </div>
                          <Button size="xs" variant="danger" icon={Trash2} onClick={() => removeCustomPeriod(i)} />
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ padding: '1rem', background: 'var(--bg-overlay)', borderRadius: 'var(--radius-sm)', border: '1px dashed var(--border-subtle)' }}>
                    <Input label="Period Label" value={newPeriodLabel} onChange={e => setNewPeriodLabel(e.target.value)} placeholder="e.g. Q1 Apr–Jun" />
                    <div className="field" style={{ marginTop: '0.75rem' }}>
                      <label className="field-label">Select Months</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem' }}>
                        {MONTHS_SHORT.map((m, idx) => {
                          const sel = newPeriodMonths.includes(idx);
                          return (
                            <button key={idx} type="button" onClick={() => toggleCustomMonth(idx)}
                              style={{ padding: '0.3rem 0.65rem', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', border: `1px solid ${sel ? 'var(--gold)' : 'var(--border-subtle)'}`, background: sel ? 'var(--gold-glow)' : 'var(--bg-raised)', color: sel ? 'var(--gold)' : 'var(--text-muted)', fontWeight: sel ? 700 : 400, cursor: 'pointer' }}>
                              {m}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <Button size="sm" icon={Plus} onClick={addCustomPeriod} style={{ marginTop: '0.85rem' }}>Add Period</Button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                <Button loading={freqSaving} onClick={handleFreqSave}>Save Billing Settings</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ TAB: Terms & Conditions ══ */}
      {activeTab === 'terms' && (
        <div>
          {termsLoading ? <div className="loading-center"><div className="spinner" /></div> : (
            <div className="section-card">
              <div className="section-card-head">
                <h3>Terms & Conditions</h3>
                <Button size="sm" loading={termsSaving} onClick={handleTermsSave}>Save</Button>
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
                These terms will appear at the bottom of every printed maintenance bill.
              </p>
              <textarea
                value={terms}
                onChange={e => setTerms(e.target.value)}
                rows={12}
                placeholder="Enter your society's terms and conditions..."
                style={{ width: '100%', padding: '0.85rem 1rem', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', fontSize: '0.85rem', lineHeight: 1.7, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }}
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.5rem' }}>
                Each line will appear as a separate point on the bill.
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <Button loading={termsSaving} onClick={handleTermsSave}>Save Terms</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: Email Settings ══ */}
      {activeTab === 'email' && (
        <div>
          {emailLoading ? <div className="loading-center"><div className="spinner" /></div> : (
            <div className="section-card">
              <div className="section-card-head">
                <h3>Email Settings</h3>
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                Maintenance bills are sent from <strong>billing@drusshti.com</strong> on behalf of your society.
                Set your society's Gmail below so members can reply directly to you.
              </p>

              <div style={{ maxWidth: 480 }}>
                <Input
                  label="Society Reply-To Email (Gmail)"
                  type="email"
                  value={replyToEmail}
                  onChange={e => setReplyToEmail(e.target.value)}
                  placeholder="e.g. sunflowerchs@gmail.com"
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.4rem', marginBottom: '1.25rem' }}>
                  When members receive a bill email and hit Reply, their response will go to this address.
                </div>

                <div style={{ padding: '0.85rem 1rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--text)' }}>What members will see in their inbox:</div>
                  <div><strong>From:</strong> {'{Society Name}'} &lt;billing@drusshti.com&gt;</div>
                  <div style={{ marginTop: 4 }}><strong>Reply-To:</strong> {replyToEmail || 'not set'}</div>
                </div>

                <Button loading={emailSaving} onClick={handleEmailSave}>Save Email Settings</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Add/Edit Charge Head Modal ── */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editHead ? 'Edit Charge Head' : 'Add Charge Head'} size="md"
        footer={
          <>
            <Button variant="subtle" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>{editHead ? 'Update' : 'Add'}</Button>
          </>
        }>
        <div className="txn-form">
          <Input label="Charge Head Name *" value={form.name} onChange={sf('name')} placeholder="e.g. Service Charges" />
          <div className="txn-form-grid">
            <Input label="Code *" value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="e.g. SVC" maxLength={10} />
            <Input label="Display Order" type="number" value={form.displayOrder} onChange={sf('displayOrder')} />
          </div>

          <div className="field">
            <label className="field-label">
              YEA Income Head
              <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', fontWeight: 400, marginLeft: '0.4rem' }}>
                (receipts for this head auto-credit this YEA account)
              </span>
            </label>
            <div className="field-control">
              {yeaHeads.length === 0 ? (
                <div style={{ padding: '0.62rem 0.9rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', fontSize: '0.82rem', color: 'var(--text-dim)' }}>
                  No YEA income heads found — add them in Year End Accounting first
                </div>
              ) : (
                <select className="field-input" value={form.yea_incomeHead} onChange={sf('yea_incomeHead')}>
                  <option value="">— Not mapped —</option>
                  {yeaHeads.map(h => <option key={h._id} value={h.name}>{h.name}</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="txn-form-grid">
            <div className="field">
              <label className="field-label">GST Applicable?</label>
              <div className="field-control">
                <select className="field-input" value={form.isGSTApplicable ? 'yes' : 'no'}
                  onChange={e => setForm(f => ({ ...f, isGSTApplicable: e.target.value === 'yes' }))}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
            {form.isGSTApplicable && (
              <Input label="GST Rate (%)" type="number" value={form.gstRate} onChange={sf('gstRate')} />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
